import { ActiveUserStore } from '@active-user-management';
import { afterNextRender, computed, effect, inject, Injectable, Injector, signal } from '@angular/core';
import { AuthStore } from '@auth/data-access';
import { CompanySettingsStore } from '@company-settings/data-access';
import { UserModel } from '@core/api/data-access';
import { StorageKeys } from '@core/storage';
import { UserStore } from '@data-users';
import { localStorageSignal } from '@ddtmm/angular-signal-generators';
import { TranslateService } from '@ngx-translate/core';
import { environment, isSupportedLanguage, LanguageType, SupportedLanguageType } from '@shared-types';
import { ToastService } from '@utils';
import { PrimeNGConfig } from 'primeng/api';
import { catchError, firstValueFrom, of } from 'rxjs';
import { getPrimeNgTranslation } from '../primeng';
import { Language } from './../types/language.structure';
import ILanguage = Language.ILanguage;

/**
 * active language priority:
 * 1. user preferences (from the login response => user.preferences.language)
 * 2. company settings (company settings => defaultLanguage)
 * 3. app default (if navigator.language is supported by the app ['he','en','es'] then use it, otherwise use 'en')
 *
 *
 * * when user login, we need to check:
 * - if the user.preferences.language is set and it's not 'default', then we use it
 * - if the user.preferences.language is set to 'default':
 * 	- wait for the company settings to be loaded
 * 	- if the company settings are loaded and the defaultLanguage is set, then we use it
 * 	- if the company settings are loaded and the defaultLanguage is not set, then we use the app default
 * - if the user.preferences.language is not set, then we use the app default
 *
 */

export const AppLanguageSignal$ = localStorageSignal<SupportedLanguageType>(LanguageType.English, StorageKeys.GlobalKeys.LANGUAGE);

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	/* ------------ Providers / Services ------------ */
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly authStore: AuthStore = inject(AuthStore);
	private readonly userStore: UserStore = inject(UserStore);
	private readonly companySettingsStore = inject(CompanySettingsStore);
	private readonly primeNGConfig: PrimeNGConfig = inject(PrimeNGConfig);
	private readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);
	private readonly toastService: ToastService = inject(ToastService);
	private readonly injector: Injector = inject(Injector);
	/* ---------------- Properties ---------------- */

	/* ---------------- Signals ---------------- */
	public readonly currentLang$ = computed<SupportedLanguageType>(() => AppLanguageSignal$());
	public readonly currentLangData = computed<ILanguage>(() => Language.Languages[this.currentLang$()]);
	public readonly currentUpdateLang$ = signal<LanguageType | null>(null);
	public readonly isLanguageChanging$ = signal<boolean>(false);
	private readonly primengConfigLanguage$ = signal<SupportedLanguageType>(null);

	/* ---------------- Constructor ---------------- */
	constructor() {
		// Initialize immediately for essential setup
		this.initializeLanguageOnStartup();

		// Defer reactive effects until after render to ensure all dependencies are ready
		afterNextRender(() => {
			this.initReactiveEffects();
		});
	}

	/**
	 * Initialize reactive effects for language management
	 * This runs after the initial render to ensure all dependencies are ready
	 */
	private initReactiveEffects(): void {
		// Implementation using signals for reactive language updates
		effect(() => {
			const isLoggedIn = this.authStore.isLoggedIn();
			const inLoginProcess = this.authStore.inLoginProcess();

			// If in login process, wait until it's finished
			if (inLoginProcess) {
				return;
			}

			// If not logged in, use default language (but don't override already set language)
			if (!isLoggedIn) {
				const currentLang = this.currentLang$();
				if (!currentLang) {
					const defaultLanguage = this.getDefaultLanguage();
					if (isSupportedLanguage(defaultLanguage)) {
						this.setAppLanguageSync(defaultLanguage);
					}
				}
				return;
			}

			// User is logged in, determine language based on priority:
			const activeUser = this.activeUserStore.user$();

			// 1. Check user preferences
			if (activeUser && activeUser.preferences.language) {
				// If user has specific language preference (not 'default')
				if (activeUser.preferences.language !== 'default') {
					const userLanguage = LanguageService.validateLanguage(activeUser.preferences.language);
					if (userLanguage) {
						this.setAppLanguageSync(userLanguage);
						return;
					}
				}

				// 2. If user preference is 'default' or invalid, check company settings
				const companySettings = this.companySettingsStore.companySettings$();
				if (companySettings && companySettings.defaultLanguage) {
					const companyLanguage = LanguageService.validateLanguage(companySettings.defaultLanguage);
					if (companyLanguage) {
						this.setAppLanguageSync(companyLanguage);
						return;
					}
				}
			}

			// 3. Fallback to app default if user preferences and company settings don't provide a valid language
			const defaultLanguage = this.getDefaultLanguage();
			if (isSupportedLanguage(defaultLanguage)) {
				this.setAppLanguageSync(defaultLanguage);
			}
		}, { injector: this.injector });
	}

	/**
	 * Initializes language on application startup from localStorage
	 * This ensures immediate language availability without waiting for async operations
	 */
	private initializeLanguageOnStartup(): void {
		const storedLanguage = AppLanguageSignal$();

		// Always start with English as fallback to ensure translations work
		this.translateService.setDefaultLang(LanguageType.English);

		if (storedLanguage && isSupportedLanguage(storedLanguage)) {
			this.setAppLanguageSync(storedLanguage);
		} else {
			// If no valid stored language, use default
			const defaultLanguage = this.getDefaultLanguage();
			if (isSupportedLanguage(defaultLanguage)) {
				this.setAppLanguageSync(defaultLanguage);
			} else {
				// Ultimate fallback to English
				this.setAppLanguageSync(LanguageType.English);
			}
		}
	}

	/**
	 * Synchronous version of setAppLanguage for initial startup
	 * This doesn't update user preferences on server
	 */
	private setAppLanguageSync(language: SupportedLanguageType): void {
		try {
			const languageData: ILanguage = Language.Languages[language];

			if (!languageData) {
				console.error(`Language data not found for: ${language}`);
				return;
			}



			// Update document attributes immediately
			this.updateDocumentAttributes(languageData);

			// Update CSS variables for direction
			this.updateCssVariables(languageData);

			// Update environment direction
			if (environment.direction !== languageData.dir) {
				environment.direction = languageData.dir;
			}

			// Update the app language signal
			AppLanguageSignal$.set(language);

			// Set the translate service language immediately
			this.translateService.setDefaultLang(language);

			// Force immediate translation loading and ensure it's properly loaded
			let translateSubscription: any;
			translateSubscription = this.translateService.use(language).subscribe({
				next: (translations) => {
					// Verify translations are actually loaded
					if (!translations || Object.keys(translations).length === 0) {
						// Try to reload after a short delay
						setTimeout(() => {
							this.translateService.reloadLang(language);
						}, 100);
					}
				},
				error: (error) => {
					console.error(`Failed to load translation files for ${language}:`, error);
					// Fallback to English if current language fails to load
					if (language !== LanguageType.English) {
						this.translateService.setDefaultLang(LanguageType.English);
						this.translateService.use(LanguageType.English);
						AppLanguageSignal$.set(LanguageType.English);
					}
				},
				complete: () => {
					translateSubscription?.unsubscribe();
				}
			});

			// Update PrimeNG config for initial load
			this.updatePrimeNGConfigSync(language);
		} catch (error) {
			console.error('Failed to set initial language:', error);
			// Fallback to English if anything fails
			this.translateService.setDefaultLang(LanguageType.English);
			this.translateService.use(LanguageType.English);
			AppLanguageSignal$.set(LanguageType.English);
		}
	}

	/**
	 * Sets the application language with optimized approach (no page reload)
	 * @param language - Target language
	 * @param updateUserPreferences - Whether to update user preferences on server
	 */
	public async setAppLanguage(language: LanguageType, updateUserPreferences = false): Promise<boolean> {
		// Prevent multiple concurrent language changes
		if (this.isLanguageChanging$()) {
			console.warn('Language change already in progress, skipping...');
			return false;
		}

		// Check if we're already on this language
		if (this.currentLang$() === language) {
			return true;
		}

		// Validate language support
		if (!isSupportedLanguage(language)) {
			console.error(`Language ${language} is not supported by the app`);
			return false;
		}

		this.isLanguageChanging$.set(true);
		this.currentUpdateLang$.set(language);

		try {
			// Apply language changes without page reload
			const success = await this.setLanguageOptimized(language);

			if (success && updateUserPreferences) {
				await this.updateActiveUserLanguage(language);
			}

			return success;
		} catch (error) {
			console.error('Failed to change language:', error);

			// Show user-friendly error message
			this.toastService.error('language.change.failed');

			// Fallback to page reload only in extreme cases
			if (this.shouldFallbackToReload(error)) {
				console.warn('Falling back to page reload due to critical error');
				this.fallbackToReload();
				return true;
			}

			return false;
		} finally {
			this.isLanguageChanging$.set(false);
			this.currentUpdateLang$.set(null);
		}
	}

	/**
	 * Optimized language change without page reload
	 */
	private async setLanguageOptimized(language: SupportedLanguageType): Promise<boolean> {
		const languageData: ILanguage = Language.Languages[language];

		if (!languageData) {
			throw new Error(`Language data not found for: ${language}`);
		}



		// 1. Update document attributes immediately
		this.updateDocumentAttributes(languageData);

		// 2. Update CSS variables for direction
		this.updateCssVariables(languageData);

		// 3. Update environment direction
		if (environment.direction !== languageData.dir) {
			environment.direction = languageData.dir;
		}

		// 4. Update PrimeNG translations
		if (this.primengConfigLanguage$() !== language) {
			await this.updatePrimeNGConfig(language);
		}

		// 5. Update ngx-translate
		const currentTranslationLang = this.translateService.currentLang;
		if (currentTranslationLang !== language) {
			await firstValueFrom(this.translateService.use(language));
		}

		// 6. Update the app language signal (triggers reactive updates)
		AppLanguageSignal$.set(language);

		// 7. Force change detection for immediate UI updates
		this.triggerChangeDetection();

		return true;
	}

	/**
	 * Updates document HTML attributes for language and direction
	 */
	private updateDocumentAttributes(languageData: ILanguage): void {
		const docElement = document.documentElement;

		if (docElement.getAttribute('lang') !== languageData.locale_code) {
			docElement.setAttribute('lang', languageData.locale_code);
		}

		if (docElement.getAttribute('dir') !== languageData.dir) {
			docElement.setAttribute('dir', languageData.dir);
		}
	}

	/**
	 * Updates CSS custom properties for direction handling
	 */
	private updateCssVariables(languageData: ILanguage): void {
		const docElement = document.documentElement;
		const currentDir = docElement.style.getPropertyValue('--dc-dir');

		if (currentDir !== languageData.dir) {
			docElement.style.setProperty('--dc-dir', languageData.dir);
			docElement.style.setProperty('--dc-opposite-dir', languageData.oppositeLang.dir);
		}
	}

	/**
	 * Triggers change detection across the application
	 */
	private triggerChangeDetection(): void {
		// The signal update will automatically trigger change detection
		// in all components that use the language signals

		// Force a microtask to ensure all pending updates are processed
		Promise.resolve();
	}

	/**
	 * Fallback method - only used in extreme error cases
	 */
	private fallbackToReload(): void {
		console.warn('Performing fallback page reload for language change');

		// Save current state before reload (with quota error handling)
		try {
			sessionStorage.setItem('dc-language-fallback', 'true');
			sessionStorage.setItem('dc-language-fallback-time', Date.now().toString());
		} catch (e) {
			if (e.name === 'QuotaExceededError') {
				try {
					// Clear some non-critical storage items
					sessionStorage.removeItem('dc-language-fallback-time');
					localStorage.removeItem('debug-logs');
					// Try again with minimal data
					sessionStorage.setItem('dc-language-fallback', 'true');
				} catch (e2) {
					// Ignore if still can't store
				}
			}
		}

		window.location.reload();
	}

	/**
	 * Determines if we should fallback to page reload based on error type
	 */
	private shouldFallbackToReload(error: any): boolean {
		// Only fallback for critical errors that prevent basic functionality
		const criticalErrors = [
			'Failed to load translation files',
			'TranslateService not available',
			'Critical DOM manipulation error'
		];

		return criticalErrors.some(criticalError =>
			error?.message?.includes(criticalError)
		);
	}

	/**
	 * Updates the active user's language preference on the server
	 */
	private async updateActiveUserLanguage(language: LanguageType): Promise<UserModel | null> {
		const activeUser = this.activeUserStore.user$() || null;
		if (!activeUser) {
			console.warn('No active user found, cannot update language preference');
			return null;
		}

		try {


			// Update the user object
			activeUser.preferences.language = language;

			// Save to server
			const updatedUser = await firstValueFrom(this.userStore.updateUser(activeUser, undefined, true).pipe(catchError(() => {
				this.toastService.warn('language.preferences.updateFailed');
				return of(null);
			})));

			if (updatedUser) {
				// Update local store
				this.activeUserStore.updateActiveUserState(updatedUser, false);
			}

			return updatedUser;
		} catch (error) {
			console.error('Failed to update user language preference:', error);

			// Handle specific error types
			if (error.name === 'QuotaExceededError') {
				this.toastService.warn('language.storage.quotaExceeded');
			} else {
				// Show user notification but don't fail the language change
				this.toastService.warn('language.preferences.updateFailed');
			}

			return null;
		}
	}

	private getDefaultLanguage(): SupportedLanguageType {
		const localLanguage = AppLanguageSignal$();
		if (localLanguage) return localLanguage;
		const navigatorLanguage = navigator.language;
		return LanguageService.normalizeLanguage(navigatorLanguage) ?? LanguageType.English;
	}

	private updatePrimeNGConfig(language: SupportedLanguageType): Promise<boolean> {
		if (this.primengConfigLanguage$() === language) return Promise.resolve(true);
		return getPrimeNgTranslation(language)
			.catch(error => {
				console.error(`Error loading primeng translations for language ${language}:`, error);
				return false;
			})
			.then(translation => {
				this.primeNGConfig.setTranslation(translation);
				this.primengConfigLanguage$.set(language);
				return true;
			});
	}

	/**
	 * Synchronous version of PrimeNG config update for initial startup
	 */
	private updatePrimeNGConfigSync(language: SupportedLanguageType): void {
		try {
			if (this.primengConfigLanguage$() === language) {
				return;
			}

			// Get translation synchronously (assuming it's already loaded)
			const primeNgTranslation = getPrimeNgTranslation(language);

			// If getPrimeNgTranslation returns a Promise, handle it
			if (primeNgTranslation instanceof Promise) {
				primeNgTranslation
					.then(translation => {
						if (translation) {
							this.primeNGConfig.setTranslation(translation);
							this.primengConfigLanguage$.set(language);
						}
					})
					.catch(error => {
						console.warn(`Failed to load PrimeNG translation for ${language} during startup:`, error);
					});
			} else {
				// Direct translation object
				if (primeNgTranslation) {
					this.primeNGConfig.setTranslation(primeNgTranslation);
					this.primengConfigLanguage$.set(language);
				}
			}
		} catch (error) {
			console.error('Failed to update PrimeNG configuration during startup:', error);
		}
	}

	public static validateLanguage(language: string): SupportedLanguageType | null {
		if (isSupportedLanguage(language)) {
			return language as SupportedLanguageType;
		}
		return null;
	}

	public static normalizeLanguage(language: string): SupportedLanguageType | null {
		if (isSupportedLanguage(language)) {
			return language as SupportedLanguageType;
		}
		// example: 'en-US' => 'en'| 'he-IL' => 'he' | 'es-ES' => 'es'
		const normalizedLanguage = language.split('-')?.at(0)?.toLowerCase();
		if (isSupportedLanguage(normalizedLanguage)) {
			return normalizedLanguage as SupportedLanguageType;
		}
		return null;
	}


}
